/**
 * Wspólne źródło URL-i — używane przez sitemap.ts (api-server)
 * oraz przyszły runner prerenderowania (scripts).
 *
 * Eksportuje wyłącznie czyste dane i funkcje bez zewnętrznych zależności:
 *   - stałe i listy statycznych URL-i
 *   - zapytania GraphQL do Shopify Storefront API
 *   - typy i narzędzia do konwersji handles → public slugi
 *
 * NIE eksportuje: fetcherów HTTP, klientów Shopify, helperów XML, cache.
 */

// ─── Stała bazowa ─────────────────────────────────────────────────────────────

import { BOUQUET_CREATOR_ENABLED, BOUQUET_CREATOR_PATHS, isUnavailableBouquetCreatorPath } from './storeFeatures';
export { BOUQUET_CREATOR_ENABLED, BOUQUET_CREATOR_PATHS, KREATOR_SLUGS, isBouquetCreatorPath, isUnavailableBouquetCreatorPath, filterAvailableSiteUrlEntries } from './storeFeatures';

export const PROD_ORIGIN = "https://bukietowa.pl";

/** Public URLs are directory-backed by Cloudflare Assets, so use one trailing slash. */
export function canonicalPath(path: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return normalized === "/" ? "/" : `${normalized.replace(/\/+$/, "")}/`;
}

// ─── Statyczne strony ─────────────────────────────────────────────────────────

export interface StaticPage {
  path: string;
  changefreq: string;
  priority: string;
}

export const STATIC_PAGES: StaticPage[] = [
  { path: "/",                        changefreq: "daily",   priority: "1.0" },
  { path: "/sklep",                   changefreq: "daily",   priority: "0.9" },
  { path: "/blog",                    changefreq: "weekly",  priority: "0.8" },
  { path: "/kreatory-bukietow",       changefreq: "weekly",  priority: "0.8" },
  { path: "/bestsellery",             changefreq: "weekly",  priority: "0.8" },
  { path: "/dostawa",                 changefreq: "monthly", priority: "0.6" },
  { path: "/platnosci",               changefreq: "monthly", priority: "0.6" },
  { path: "/kontakt",                 changefreq: "monthly", priority: "0.6" },
  { path: "/o-nas",                   changefreq: "monthly", priority: "0.6" },
  { path: "/faq",                     changefreq: "monthly", priority: "0.6" },
  { path: "/dla-firm",                changefreq: "monthly", priority: "0.7" },
  { path: "/zamowienie-specjalne",    changefreq: "monthly", priority: "0.6" },
  { path: "/warsztaty-florystyczne",  changefreq: "monthly", priority: "0.6" },
  { path: "/dla-kogo",                changefreq: "weekly",  priority: "0.8" },
  { path: "/kwiaciarnia-lublin",      changefreq: "weekly",  priority: "0.8" },
  { path: "/kwiaciarnia-okolice",     changefreq: "weekly",  priority: "0.8" },
  // Dedykowane strony edytorskie/SEO — NIE kolekcje Shopify
  { path: "/slub",                    changefreq: "monthly", priority: "0.8" },
].map((page) => ({ ...page, path: canonicalPath(page.path) }))
  .filter(page => !isUnavailableBouquetCreatorPath(page.path));

/**
 * Publiczne strony, które potrzebują statycznego snapshotu, lecz z założenia
 * nie są przeznaczone do indeksacji i nie trafiają do sitemap.
 */
export const NON_INDEXABLE_PRERENDER_PAGES: StaticPage[] = [
  { path: "/regulamin",          changefreq: "yearly", priority: "0.3" },
  { path: "/prywatnosc",         changefreq: "yearly", priority: "0.3" },
  { path: "/polityka-zwrotow",   changefreq: "yearly", priority: "0.3" },
  ...(!BOUQUET_CREATOR_ENABLED ? BOUQUET_CREATOR_PATHS.map(path => ({ path, changefreq: 'monthly', priority: '0.1' })) : []),
].map((page) => ({ ...page, path: canonicalPath(page.path) }));

/** Zbiór ścieżek już pokrytych przez STATIC_PAGES — do deduplikacji z kolekcjami Shopify. */
const STATIC_PAGE_PATHS = new Set(STATIC_PAGES.map((p) => p.path));

// ─── Blog ─────────────────────────────────────────────────────────────────────

export const BLOG_SLUGS: string[] = [
  "jakie-kwiaty-podarowac-z-okazji-dnia-kobiet",
  "kwiaty-doniczkowe-dla-poczatkujacych",
  "najnowsze-trendy-florystyczne-na-sluby-i-wesela",
  "kwiaty-ciete-jak-przedluzyc-ich-trwalosc",
  "walentynki",
  "kwiatowe-dekoracje-stolow-na-przyjecie-weselne-pomysly-i-porady",
  "kwiatowe-akcenty-w-zaproszeniach-i-papeterii-slubnej-inspiracje-i-wskazowki",
  "jak-pielegnowac-rosliny-doniczkowe-podstawy-dla-poczatkujacych",
  "jak-dbac-o-kwiaty-ciete-poznaj-10-sprawdzonych-porad",
  "kolory-kwiatow-i-ich-znaczenie",
  "jakie-kwiaty-na-pierwsza-randke",
  "jakie-sa-najtrwalsze-kwiaty-ciete",
  "kwiaty-na-pierwsze-spotkanie-idealny-wybor",
  "rozowe-tulipany-znaczenie-przyjazni-i-wdziecznosci",
  "jakie-kwiaty-na-urodziny-wybrac-dla-dziewczyny",
  "sloneczniki-symbolika-szczescia-i-radosci",
  "kwiaty-sezonowe-jakie-bukiety-wybierac-wiosna-latem-jesienia-i-zima",
  "kwiaty-w-biznesie-jak-wybrac-idealny-bukiet-do-biura-lub-na-firmowe-wydarzenie",
  "flowerboxy-nowoczesny-sposob-na-prezent-z-kwiatow",
  "jak-wybrac-kwiaty-na-przeprosiny-bukiet-ktory-przemowi-za-ciebie",
  "suszone-kwiaty-diy-jak-samodzielnie-stworzyc-bukiet-ktory-przetrwa-lata",
  "ile-roz-kupic-symbolika-liczby-kwiatow-w-bukiecie",
  "kwiaty-bezpieczne-dla-kotow-i-psow-10-gatunkow-przyjaznych-zwierzetom",
  "kwiaty-na-chrzciny-5-subtelnych-bukietow-i-dekoracji-ktore-podkresla-wyjatkowy-dzien",
  "flowerboxy-i-kwiaty-w-pudelku",
  "flowerbox-czy-klasyczny-bukiet-wybor-nalezy-do-ciebie",
  "jak-przedluzyc-swiezosc-kwiatow-cietych-przewodnik-krok-po-kroku-2025",
  "znaczenie-kolorow-kwiatow-co-symbolizuja-roze-tulipany-i-lilie",
  "kwiaty-na-imieniny-co-wybrac-zamiast-tradycyjnych-roz-5-pomyslow",
  "bukiet-dla-mezczyzny-jak-zlamac-stereotyp-i-wybrac-kwiaty-ktore-sie-spodobaja",
  "jak-dbac-o-kwiaty-podczas-wakacji-i-urlopu",
  "jak-zamawiac-kwiaty-online-praktyczny-przewodnik",
  "jak-przedluzyc-swiezosc-kwiatow-w-wazonie-7-sprawdzonych-sposobow",
  "bukiet-slubny-2026-trendy-i-pomysly-na-niezapomniany-dzien",
];

/** Canonical public URL for a blog article. */
export function articleUrl(slug: string): string {
  const normalizedSlug = normalizeHandle(slug).replace(/^\/+|\/+$/g, "");
  return canonicalPath(normalizedSlug);
}

/**
 * Legacy paths that must be redirected at the HTTP layer.
 *
 * Keep destinations canonical and trailing-slash-normalized because
 * Cloudflare Assets serves prerendered route directories directly at the
 * trailing-slash form.
 */
export const LEGACY_REDIRECTS: Record<string, string> = {
  "/kwiaty/lwiepaszcze/": "/kolekcje/lwie-paszcze/",
  "/kolekcje/lwiepaszcze/": "/kolekcje/lwie-paszcze/",
  "/florystyka-slubna/dekoracja-sali-weselnej/": "/slub/",
  "/okazja/kwiaty-na-slub/": "/slub/",
  "/okazja/walentynki/": "/okazje/walentynki/",
  "/produkt/flowerbox-pastelowe-love/": "/flower-boxy/",
  // Numbered WooCommerce products have no verified one-to-one product match.
  // Keep their existing ad traffic within the corresponding current category.
  "/produkt/wiazanka-nr-1/": "/kompozycje-pogrzebowe/wiazanki-pogrzebowe/",
  "/produkt/wiazanka-nr-2/": "/kompozycje-pogrzebowe/wiazanki-pogrzebowe/",
  "/produkt/wiazanka-nr-3/": "/kompozycje-pogrzebowe/wiazanki-pogrzebowe/",
  "/produkt/wiazanka-nr-4/": "/kompozycje-pogrzebowe/wiazanki-pogrzebowe/",
  "/produkt/wiazanka-nr-5/": "/kompozycje-pogrzebowe/wiazanki-pogrzebowe/",
  "/produkt/wiazanka-nr-6/": "/kompozycje-pogrzebowe/wiazanki-pogrzebowe/",
  "/produkt/wiazanka-nr-7/": "/kompozycje-pogrzebowe/wiazanki-pogrzebowe/",
  "/produkt/wiazanka-nr-8/": "/kompozycje-pogrzebowe/wiazanki-pogrzebowe/",
  "/produkt/wiazanka-nr-9/": "/kompozycje-pogrzebowe/wiazanki-pogrzebowe/",
  "/produkt/wiazanka-nr-10/": "/kompozycje-pogrzebowe/wiazanki-pogrzebowe/",
  "/produkt/wiazanka-nr-11/": "/kompozycje-pogrzebowe/wiazanki-pogrzebowe/",
  "/produkt/wieniec-nr-1/": "/kompozycje-pogrzebowe/wience-pogrzebowe/",
  "/produkt/wieniec-nr-2/": "/kompozycje-pogrzebowe/wience-pogrzebowe/",
  "/produkt/wieniec-nr-3/": "/kompozycje-pogrzebowe/wience-pogrzebowe/",
  "/sklep/bukiety/": "/bukiety/",
  "/sklep/bukiety/bukiety-jednorodne/roze/": "/kwiaty/roze/",
  "/sklep/bukiety/bukiety-jednorodne/sloneczniki/": "/kwiaty/sloneczniki/",
  "/sklep/florystyka/": "/kwiaty-doniczkowe/",
  "/sklep/flowerbox/": "/flower-boxy/",
  "/sklep/okazje/": "/okazje/",
  "/sklep/bukiety-kwiatow/bukiety-jednorodne/bukiety-roz/": "/kwiaty/roze/",
  "/sklep/bukiety-kwiatow/bukiety-jednorodne/bukiety-tulipanow/": "/kwiaty/tulipany/",
  "/sklep/bukiety-kwiatow/bukiety-jednorodne/bukiet-gozdzikow/": "/kwiaty/gozdziki/",
  // Creators are temporarily unavailable; send legacy ads to the active offer.
  "/sklep/bukiety-kwiatow/kreator/roze-lublin-bukiet-roz-z-dostawa-idealny-upominek/": "/kwiaty/roze/",
  "/sklep/bukiety-kwiatow/kreator/tulipany/": "/kwiaty/tulipany/",
  "/flowerbox/": "/flower-boxy/",
  "/okazje/zareczynny/": "/okazje/zareczyny/",
  "/okazje/dla-pary-mlodej/": "/dla-kogo/pary-mlodej/",
  "/kwiaciarnia-okolice/szerokie/": "/kwiaciarnia-lublin/szerokie/",
  "/okazje/dla-mamy/": "/dla-kogo/mamy/",
  "/okazje/dla-zony/": "/dla-kogo/zony/",
  "/okazje/dla-dziewczyny/": "/dla-kogo/dziewczyny/",
  "/okazje/dla-babci/": "/dla-kogo/babci/",
  "/okazje/dla-mezczyzny/": "/dla-kogo/mezczyzny/",
  "/okazje/dla-przyjaciela/": "/dla-kogo/przyjaciela/",
  "/bukiety-pogrzebowe/": "/kompozycje-pogrzebowe/bukiety-pogrzebowe/",
  "/wience-pogrzebowe/": "/kompozycje-pogrzebowe/wience-pogrzebowe/",
  "/wiazanki-pogrzebowe/": "/kompozycje-pogrzebowe/wiazanki-pogrzebowe/",
  "/sztuczne-kompozycje/": "/kompozycje-pogrzebowe/sztuczne-kompozycje/",
};

/** Root-level slugs owned by explicit application routes, not blog articles. */
export const ROOT_RESERVED_SLUGS = new Set<string>([
  ...STATIC_PAGES.map(page => page.path.replace(/^\/|\/$/g, "")),
  "kwiaty",
  "bukiety",
  "flower-boxy",
  "kosze-kwiatowe",
  "kwiaty-w-torebce",
  "lasy-w-szkle",
  "wieczne-roze",
  "kwiaty-doniczkowe",
  "balony-z-helem",
  "okazje",
  "kompozycje-pogrzebowe",
  "bukiety-pogrzebowe",
  "wience-pogrzebowe",
  "wiazanki-pogrzebowe",
  "sztuczne-kompozycje",
  "bestsellery",
  "sklep",
  "konto",
  "checkout",
  "dziekujemy",
  "kontakt",
  "regulamin",
  "prywatnosc",
  "reset-hasla",
  "ulubione",
  "koszyk",
]);

export interface BlogSlugConflict {
  slug: string;
  reason: "product" | "reserved";
  productHandle?: string;
}

/**
 * Find collisions before a sitemap/prerender release can publish them.
 * Product handles are normalized in the same way as public URL inputs.
 */
export function findBlogSlugConflicts(
  blogSlugs: readonly string[],
  productHandles: readonly string[],
): BlogSlugConflict[] {
  const normalizedProducts = new Map(
    productHandles.map(handle => [normalizeHandle(handle), handle]),
  );

  return [...new Set(blogSlugs.map(normalizeHandle))]
    .flatMap((slug): BlogSlugConflict[] => {
      const productHandle = normalizedProducts.get(slug);
      if (productHandle) {
        return [{ slug, reason: "product", productHandle }];
      }
      if (ROOT_RESERVED_SLUGS.has(slug)) {
        return [{ slug, reason: "reserved" }];
      }
      return [];
    });
}

// ─── Kreatory bukietów ────────────────────────────────────────────────────────

// Creator slugs and availability are shared with the storefront route gate above.

// ─── Local SEO — dzielnice Lublina ────────────────────────────────────────────

export const DISTRICT_SLUGS: string[] = [
  "srodmiescie", "stare-miasto", "wieniawa", "rury", "bronowice",
  "ponikwoda", "kalinowszczyzna", "slawin", "slawinek", "tatary",
  "czuby-polnocne", "czuby-poludniowe", "weglin-polnocny", "weglin-poludniowy",
  "kosminek", "szerokie", "za-cukrownia", "felin", "hajdow-zadebie",
  "konstantynow", "dziesiata", "czechow-polnocny", "czechow-poludniowy",
  "abramowice", "glusk", "zemborzyce", "wrotkow",
];

// ─── Local SEO — okolice Lublina ──────────────────────────────────────────────

export const NEARBY_SLUGS: string[] = [
  "swidnik", "jastkow", "konopnica", "niemce", "wolka", "spiczyn",
  "melgiew", "bychawa", "piaski", "belzyce", "naleczow", "lubartow",
  "leczna", "niedrzwica-duza", "wojciechow", "jablonna", "puchaczow",
  "trawniki", "borzechow", "strzyzewice", "turka", "dabrowica",
  "motycz", "motycz-lesny", "uniszowice", "tomaszowice", "snopkow",
  "plouszowice", "panienszczyzna", "marysin", "jakubowice-koninskie",
  "jakubowice-murowane", "dys", "ciecierzyn", "elizowka", "rudnik",
  "sobianowice", "minkowice", "kreznica-jara",
];

// ─── Shopify: mapowanie handle → kanoniczny publiczny URL ─────────────────────
//
// JEDYNE źródło prawdy dla publicznych URL-i kolekcji — używane zarówno przez
// frontend (przez re-eksport w artifacts/bukietowa/src/lib/collectionUrl.ts,
// żaden komponent nie generuje linków do kolekcji z pominięciem tej funkcji),
// jak i przez generator sitemapy (routes-sitemap.ts). Wcześniej te dwa miejsca
// używały OSOBNYCH, rozjechanych mapowań — sitemap generował inne URL-e
// (/collections/{Capitalized-Slug}, obsługiwane tylko przez legacy redirect)
// niż te, pod którymi strona faktycznie żyje (np. /kwiaty/roze). Ujednolicone
// tutaj, żeby sitemap.xml zawsze wskazywał finalne, kanoniczne adresy.

/** Normalizuj Shopify handle (dowolna wielkość liter, polskie znaki) do małych liter ASCII. */
export function normalizeHandle(handle: string): string {
  let decoded = handle;
  try {
    decoded = decodeURIComponent(handle);
  } catch {
    // Keep malformed percent-encoded input intact; it will resolve to the
    // generic collection fallback rather than making routing throw.
  }

  return decoded
    .toLowerCase()
    .replace(/ą/g, 'a').replace(/ć/g, 'c').replace(/ę/g, 'e')
    .replace(/ł/g, 'l').replace(/ń/g, 'n').replace(/ó/g, 'o')
    .replace(/ś/g, 's').replace(/ź/g, 'z').replace(/ż/g, 'z')
    .replace(/\s+/g, '-');
}

/** Handle (znormalizowany) → publiczny URL. */
const HANDLE_TO_URL: Record<string, string> = {
  // ── Główne kategorie ───────────────────────────────────────────
  'kwiaty':                     '/kwiaty',
  'bukiety':                    '/bukiety',
  'flower-boxy':                '/flower-boxy',
  'flowerboxy':                 '/flower-boxy',
  'flower-box':                 '/flower-boxy',
  'flowerbox':                  '/flower-boxy',
  'kwiaty-doniczkowe':          '/kwiaty-doniczkowe',
  'kwiaty-doniczkowe-1':        '/kwiaty-doniczkowe',
  'bestsellery':                '/bestsellery',
  'slub':                       '/slub',
  'all':                        '/sklep',

  // ── Podkategorie kwiatowe ──────────────────────────────────────
  'roze':                       '/kwiaty/roze',
  'tulipany':                   '/kwiaty/tulipany',
  'lilie':                      '/kwiaty/lilie',
  'gerbery':                    '/kwiaty/gerbery',
  'gozdziki':                   '/kwiaty/gozdziki',
  'sloneczniki':                '/kwiaty/sloneczniki',
  'alstroemerie':               '/kwiaty/alstroemerie',
  'eustomy':                    '/kwiaty/eustomy',
  'gipsowka':                   '/kwiaty/gipsowka',
  'margaretki':                 '/kwiaty/margaretki',
  'piwonie':                    '/kwiaty/piwonie',
  'hortensje':                  '/kwiaty/hortensje',
  'lewkonie':                   '/kwiaty/lewkonie',
  'frezje':                     '/kwiaty/frezje',
  'jaskier':                    '/kwiaty/jaskier',
  'anturium':                   '/kwiaty/anturium',
  'lwiepaszcze':                '/kolekcje/lwie-paszcze',
  'dalie':                      '/kwiaty/dalie',
  'kalia':                      '/kwiaty/kalia',
  'kosze-kwiatowe':             '/kosze-kwiatowe',
  'kwiaty-w-koszu':             '/kosze-kwiatowe',
  'kwiaty-w-torebce':           '/kwiaty-w-torebce',
  'lasy-w-szkle':               '/lasy-w-szkle',
  'wieczne-roze':               '/wieczne-roze',
  'balony-z-helem':             '/balony-z-helem',

  // ── Okazje ────────────────────────────────────────────────────
  'okazje':                     '/okazje',
  'urodziny':                   '/okazje/urodziny',
  'imieniny':                   '/okazje/imieniny',
  'rocznica':                   '/okazje/rocznica',
  'przeprosiny':                '/okazje/przeprosiny',
  'gratulacje':                 '/okazje/gratulacje',
  'podziekowania':              '/okazje/podziekowania',
  'niespodzianka':              '/okazje/niespodzianka',
  'komunia':                    '/okazje/komunia',
  'obrona':                     '/okazje/obrona',
  'zareczynny':                 '/okazje/zareczyny',
  'zareczyny':                  '/okazje/zareczyny',
  'kwiaty-dla-pary-mlodej':     '/dla-kogo/pary-mlodej',
  'dla-pary-mlodej':            '/dla-kogo/pary-mlodej',
  'dla-mamy':                   '/dla-kogo/mamy',
  'dla-zony':                   '/dla-kogo/zony',
  'dla-dziewczyny':             '/dla-kogo/dziewczyny',
  'dla-babci':                  '/dla-kogo/babci',
  'dla-mezczyzny':              '/dla-kogo/mezczyzny',
  'dla-przyjaciela':            '/dla-kogo/przyjaciela',
  'walentynki':                 '/okazje/walentynki',
  'dzien-kobiet':               '/okazje/dzien-kobiet',
  'dzien-babci-i-dziadka':      '/okazje/dzien-babci-i-dziadka',
  'dzien-matki':                '/okazje/dzien-matki',
  'dzien-ojca':                 '/okazje/dzien-ojca',
  'dzien-dziecka':              '/okazje/dzien-dziecka',
  'boze-narodzenie':            '/okazje/boze-narodzenie',
  'wielkanoc':                  '/okazje/wielkanoc',
  'wszystkich-swietych':        '/okazje/wszystkich-swietych',

  // ── Kompozycje pogrzebowe ─────────────────────────────────────
  'pogrzeb':                    '/kompozycje-pogrzebowe',
  'kompozycje-pogrzebowe':      '/kompozycje-pogrzebowe',
  'bukiety-pogrzebowe':         '/kompozycje-pogrzebowe/bukiety-pogrzebowe',
  'wience':                     '/kompozycje-pogrzebowe/wience-pogrzebowe',
  'wience-pogrzebowe':          '/kompozycje-pogrzebowe/wience-pogrzebowe',
  'wiazanki':                   '/kompozycje-pogrzebowe/wiazanki-pogrzebowe',
  'wiazanki-pogrzebowe':        '/kompozycje-pogrzebowe/wiazanki-pogrzebowe',
  'sztuczne-kompozycje':        '/kompozycje-pogrzebowe/sztuczne-kompozycje',
};

/** Existing canonical category routes; aliases are deliberately deduplicated. */
export const CATEGORY_PAGE_PATHS = [...new Set(Object.values(HANDLE_TO_URL).map(canonicalPath))];

/**
 * Zwróć publiczny URL dla danego Shopify handle kolekcji.
 * Handle może zawierać polskie znaki i dowolną wielkość liter.
 * Nieznane handle → /kolekcje/{znormalizowany-handle}.
 */
export function collectionUrl(handle: string): string {
  const key = normalizeHandle(handle);
  return canonicalPath(HANDLE_TO_URL[key] ?? `/kolekcje/${key}`);
}

/** Whether a collection handle is mapped to a dedicated public landing page. */
export function isSpecialCollectionHandle(handle: string): boolean {
  return !collectionUrl(handle).startsWith("/kolekcje/");
}

export interface ProductCollectionRef {
  handle?: string;
  title?: string;
}

export interface BreadcrumbParent {
  label: string;
  href: string;
}

/**
 * Jawny priorytet handlowego kontekstu produktu. Kolejność jest stabilna
 * i nie zależy od kolejności kolekcji zwróconej przez Shopify.
 */
export const PRODUCT_BREADCRUMB_PRIORITY: ReadonlyArray<{
  handles: readonly string[];
  label: string;
  href: string;
}> = [
  { handles: ["bestsellery"], label: "Bestsellery", href: collectionUrl("bestsellery") },
  { handles: ["wience-pogrzebowe", "wience"], label: "Wieńce pogrzebowe", href: collectionUrl("wience-pogrzebowe") },
  { handles: ["wiazanki-pogrzebowe", "wiazanki"], label: "Wiązanki pogrzebowe", href: collectionUrl("wiazanki-pogrzebowe") },
  { handles: ["bukiety-pogrzebowe"], label: "Bukiety pogrzebowe", href: collectionUrl("bukiety-pogrzebowe") },
  { handles: ["sztuczne-kompozycje"], label: "Sztuczne kompozycje", href: collectionUrl("sztuczne-kompozycje") },
  { handles: ["flowerbox", "flower-box", "flowerboxy", "flower-boxy"], label: "Flower Boxy", href: collectionUrl("flowerbox") },
  { handles: ["kosze-kwiatowe", "kwiaty-w-koszu"], label: "Kosze kwiatowe", href: collectionUrl("kosze-kwiatowe") },
  { handles: ["kwiaty-w-torebce"], label: "Kwiaty w torebce", href: collectionUrl("kwiaty-w-torebce") },
  { handles: ["lasy-w-szkle"], label: "Lasy w szkle", href: collectionUrl("lasy-w-szkle") },
  { handles: ["kwiaty-doniczkowe", "kwiaty-doniczkowe-1"], label: "Kwiaty doniczkowe", href: collectionUrl("kwiaty-doniczkowe") },
  { handles: ["balony-z-helem"], label: "Balony z helem", href: collectionUrl("balony-z-helem") },
  { handles: ["wieczne-roze"], label: "Wieczne róże", href: collectionUrl("wieczne-roze") },
  { handles: ["bukiety", "bukiety-mieszane"], label: "Bukiety", href: collectionUrl("bukiety") },
  { handles: ["kreatory", "kreator-roz"], label: "Kreatory bukietów", href: canonicalPath("/kreatory-bukietow") },
  { handles: ["kwiaty"], label: "Kwiaty", href: collectionUrl("kwiaty") },
  { handles: ["okazje", "kwiaty-na-okazje"], label: "Okazje", href: collectionUrl("okazje") },
];

/** Wybiera deterministyczny parent produktu lub kanoniczny fallback /sklep/. */
export function productBreadcrumbParent(
  collections: readonly ProductCollectionRef[] | null | undefined,
): BreadcrumbParent {
  const productHandles = new Set(
    (collections ?? []).flatMap((collection) => {
      const values = [collection.handle, collection.title].filter(
        (value): value is string => Boolean(value),
      );
      return values.map(normalizeHandle);
    }),
  );

  for (const candidate of PRODUCT_BREADCRUMB_PRIORITY) {
    if (candidate.handles.some((handle) => productHandles.has(normalizeHandle(handle)))) {
      return { label: candidate.label, href: candidate.href };
    }
  }

  return { label: "Sklep", href: canonicalPath("/sklep") };
}

/** Odwrócona mapa: publiczny URL → znormalizowany handle. */
const URL_TO_HANDLE: Record<string, string> = Object.fromEntries(
  // Prefer first occurrence (canonical handle, not alias)
  Object.entries(HANDLE_TO_URL).reverse().map(([h, u]) => [u, h])
);

/**
 * Zwróć Shopify handle dla danego publicznego URL-a.
 * Zwraca null jeśli nie znaleziono (użyj useParams jako fallback).
 */
export function handleFromPublicUrl(pathname: string): string | null {
  return URL_TO_HANDLE[pathname.replace(/\/+$/, "") || "/"] ?? null;
}

/** Czy dana ścieżka jest już pokryta przez STATIC_PAGES (do deduplikacji sitemapy). */
export function isStaticPagePath(path: string): boolean {
  return STATIC_PAGE_PATHS.has(canonicalPath(path));
}

// ─── Dodatki Shopify ──────────────────────────────────────────────────────────

/**
 * Tytuły kolekcji, których produkty są dostępne wyłącznie jako dodatki do
 * głównego produktu. Nie mają samodzielnych, indeksowalnych stron produktu.
 */
export const ADDON_COLLECTION_TITLES = [
  "Wazony",
  "Pluszaki",
  "Czekoladki",
  "Toppery",
  "Alkohole",
] as const;

const NORMALIZED_ADDON_COLLECTION_TITLES = new Set(
  ADDON_COLLECTION_TITLES.map(normalizeCollectionTitle),
);

/** Normalizuje tytuł kolekcji niezależnie od wielkości liter i odstępów. */
export function normalizeCollectionTitle(title: string): string {
  return normalizeHandle(title.trim()).replace(/^-+|-+$/g, "");
}

/** Czy produkt należy do kolekcji dodatków, a więc nie ma własnej strony? */
export function isAddonProduct(product: {
  collections?: { nodes: Array<{ handle?: string; title?: string }> };
}): boolean {
  return product.collections?.nodes.some((collection) =>
    NORMALIZED_ADDON_COLLECTION_TITLES.has(
      normalizeCollectionTitle(collection.title ?? collection.handle ?? ""),
    ),
  ) ?? false;
}

/** Produkty, które mogą otrzymać samodzielny kanoniczny adres w sitemapie. */
export function indexableProducts(products: readonly ShopifyProductNode[]): ShopifyProductNode[] {
  return products.filter((product) => !isAddonProduct(product));
}

/** Canonical public URL for an indexable Shopify product. */
export function productUrl(handle: string): string {
  return canonicalPath(handle.replace(/^\/+|\/+$/g, ""));
}

// ─── Shopify GraphQL queries ──────────────────────────────────────────────────

export const ALL_PRODUCTS_QUERY = `
  query AllProducts($cursor: String) {
    products(first: 250, after: $cursor, sortKey: TITLE) {
      pageInfo { hasNextPage endCursor }
      nodes {
        handle
        updatedAt
        collections(first: 250) { nodes { title } }
      }
    }
  }
`;

export const ALL_COLLECTIONS_QUERY = `
  query AllCollections($cursor: String) {
    collections(first: 250, after: $cursor, sortKey: TITLE) {
      pageInfo { hasNextPage endCursor }
      nodes { handle updatedAt }
    }
  }
`;

// ─── Typy ─────────────────────────────────────────────────────────────────────

export interface PageInfo {
  hasNextPage: boolean;
  endCursor: string | null;
}

export interface ShopifyNode {
  handle: string;
  updatedAt: string;
}

export interface ShopifyProductNode extends ShopifyNode {
  collections: {
    nodes: Array<{ title: string }>;
  };
}

/**
 * Pojedynczy wpis URL gotowy do użycia przez sitemap.xml i prerenderer.
 * `type` identyfikuje kategorię strony — używany przez prerenderer
 * do priorytezacji i doboru strategii renderowania.
 */
export type SiteUrlType =
  | 'static'
  | 'blog'
  | 'kreator'
  | 'product'
  | 'collection'
  | 'district'
  | 'nearby';

export interface SiteUrlEntry {
  /** Ścieżka względna, np. "/", "/produkty/rozany-ogrod" */
  path: string;
  type: SiteUrlType;
  /** ISO 8601, tylko dla produktów i kolekcji */
  lastmod?: string;
  changefreq?: string;
  priority?: string;
}

/** Odpowiedź GET /sitemap-urls.json */
export interface SiteUrlsResponse {
  entries: SiteUrlEntry[];
}
